import * as React from "react"
import { useQuery } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { TrendingDown, TrendingUp } from "lucide-react"

import { LeyendaDelCorte } from "@/components/CorteReal"
import { BotonComentario } from "@/components/Comentarios"
import { DataState } from "@/components/DataState"
import { MetricCard, MetricRow } from "@/components/MetricCard"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAuth } from "@/lib/auth"
import { formatAmount, formatPercent } from "@/lib/format"
import { agrupar, bucketsDe, ultimoBucketReal } from "@/lib/periodo"
import type { Bucket } from "@/lib/periodo"
import { cn } from "@/lib/utils"

import { refCuenta } from "@/routes/comentarios/api"
import { fetchKpis, fetchPnl, fetchPnlPorArea } from "@/routes/finance/api"
import type { FuentePnl } from "@/routes/finance/mapping"
import { useFinanceFilters } from "@/routes/finance/context"
import { nombreCuenta, serieMensual } from "@/routes/finance/mapping"
import { BotonDescargar } from "@/components/BotonDescargar"
import { t } from "@/lib/i18n"


/**
 * La vista de CFO: que entra, que cuesta y que margen queda.
 *
 * Es deliberadamente OTRA pantalla que los libros. Un contador trabaja en
 * diario y mayor; quien decide mira lineas de negocio, paises y EBITDA. Mezclar
 * las dos vistas produce una pantalla que no sirve a ninguno.
 *
 * TODO viene en USD porque el modelo financiero consolida cuatro paises y tres
 * monedas al dolar con el tipo de cambio de cada mes. La moneda se dice en la
 * cabecera y en cada tabla: un importe sin moneda en una empresa multipais es
 * un importe que alguien va a leer en la suya.
 */
export function PnlPage() {
  const { activeTenantId } = useAuth()
  const { anio, pais, paisQuery, listo, granularidad } = useFinanceFilters()

  /*
    LAS TARJETAS OBEDECEN AL MANDO DE ARRIBA. No lo hacían: pedían el año y nada
    más, mientras la tabla de debajo pedía el año Y el país. Con México elegido,
    las cuatro cifras eran del grupo y la tabla era de México — la pantalla se
    veía bien y sus dos mitades hablaban de empresas distintas. `pais` entra
    también en la `queryKey`, o cambiar de sociedad devolvería la caché de la
    anterior, que es el mismo defecto con otra causa.
  */
  const kpis = useQuery({
    queryKey: ["finance-kpis", activeTenantId, anio, pais],
    queryFn: () => fetchKpis(anio, paisQuery),
    enabled: Boolean(activeTenantId) && listo,
  })
  /*
    DE DÓNDE SALEN LOS MESES PROYECTADOS.

    Última pieza del punto 7 de Cesar: que lo declarado no se quede en su
    pestaña. «Mi hoja» es la opción de salida y va a seguir siéndolo: medido
    sobre los datos de Siete, lo contratado explica entre el 14% y el 54% del
    ingreso de la hoja, así que poner lo declarado por defecto daría un P&L
    catastrófico con todos los totales cuadrando.

    Lo que lo hace valioso es poder ELEGIRLO: con la cobertura firmada al 38% en
    junio, el P&L construido sólo desde lo firmado contesta «¿cómo queda el año si
    no cierro nada más?». Antes esa pregunta no se podía hacer en ninguna
    pantalla.
  */
  const [fuente, setFuente] = React.useState<FuentePnl>("hoja")

  const pnl = useQuery({
    queryKey: ["finance-pnl", activeTenantId, anio, pais, fuente],
    queryFn: () => fetchPnl(anio, paisQuery, undefined, fuente),
    // `listo`: con una sociedad elegida, o en Holding siempre. Ver `context`.
    enabled: Boolean(activeTenantId) && listo,
  })
  const a = kpis.data?.actual
  const variacion = kpis.data?.variacion ?? {}
  const buckets = bucketsDe(granularidad, anio)
  /*
    LA LINEA ROJA. `corte.mes` es un MES y la tabla puede estar por trimestres,
    asi que la columna donde cae la linea se calcula aparte: un trimestre con un
    mes sin respaldo va del lado del forecast. Ver `ultimoBucketReal`.
  */
  const corte = pnl.data?.corte
  const bucketReal = ultimoBucketReal(buckets, corte?.mes)
  /** La serie de una cuenta del P&L, agrupada como la tabla, para la ventana de la cifra. */
  const detalleDe = (account: string) => {
    const cuenta = pnl.data?.cuentas.find((c) => c.account === account)
    if (!cuenta) return undefined
    return {
      etiquetas: buckets.map((b) => b.label),
      valores: agrupar(serieMensual(cuenta.meses), granularidad, anio).map((b) => b.valor),
      corte: bucketReal,
    }
  }

  /*
    EL P&L ARMADO. Con una frontera declarada, los meses de después no son la
    hoja: salen del libro y del maestro. Las filas y los totales se ven igual que
    siempre, así que si la pantalla no lo dice, nadie lo sabe — y el escalón que
    se ve al cruzar la frontera se lee como del negocio.
  */
  const armado = pnl.data?.ensamblado ?? null
  const armados = new Set(armado?.meses_ensamblados ?? [])
  const celdas = new Map(
    (armado?.celdas ?? []).map((c) => [`${c.account}|${c.sub_account}|${c.mes}`, c]))
  const origenDe = (account: string, sub: string, meses: number[]) => {
    if (granularidad !== "mes") return undefined
    const c = celdas.get(`${account}|${sub}|${meses[0]}`)
    if (!c) return undefined
    const quien = c.usado === "libro" ? t("el libro") : c.usado === "maestro" ? t("el maestro") : t("cada sociedad, el suyo")
    return `${t("Libro")}: ${formatAmount(c.libro)} · ${t("Maestro")}: ${formatAmount(c.maestro)} · ${t("se usó")} ${quien}`
  }

  return (
    <div className="space-y-4">
      {/* Las cuatro cifras que se miran primero. */}
      {/* Cada una se abre con su serie del año (la misma de la tabla de abajo). */}
      <MetricRow>
        <MetricCard label={t("Ventas")} value={a?.ingresos ?? 0}
                    variation={variacion.ingresos} loading={kpis.isLoading}
                    detalle={detalleDe("Revenue")} />
        <MetricCard label={t("Costo de equipo")} value={a?.costo_equipo ?? 0}
                    variation={variacion.costo_equipo} loading={kpis.isLoading} invertTrend
                    detalle={detalleDe("Labor Costs")} />
        <MetricCard label={t("Gasto operativo")} value={a?.gasto_operativo ?? 0}
                    variation={variacion.gasto_operativo} loading={kpis.isLoading} invertTrend
                    detalle={detalleDe("G&A Expenses")} />
        <MetricCard label={t("EBITDA")} value={a?.ebitda ?? 0} share={a?.ingresos}
                    variation={variacion.ebitda} loading={kpis.isLoading} tono="destacado"
                    detalle={pnl.data ? {
                      etiquetas: buckets.map((b) => b.label),
                      valores: agrupar(serieMensual(pnl.data.subtotales.ebitda.meses), granularidad, anio).map((b) => b.valor),
                      corte: bucketReal,
                    } : undefined} />
      </MetricRow>

      {/*
        CUANDO LA FUENTE NO ES LA HOJA, SE DICE Y SE DICE QUÉ MESES.

        Las dos versiones de esta tabla tienen las mismas filas y los mismos
        totales cuadrados. Sin este aviso, un P&L armado desde contratos se lee
        como el modelo de gestión — y es exactamente el defecto que esta casa
        persigue: la pantalla se ve bien y el número habla de otra cosa.
      */}
      {fuente === "declarado" && pnl.data ? (
        <p
          role="status"
          className="rounded-md border border-brand/35 bg-brand-soft px-2.5 py-2 text-2xs leading-5"
        >
          <strong className="font-medium">{t("No estás viendo tu hoja.")}</strong>{" "}
          {(pnl.data.meses_declarados ?? []).length > 0 ? (
            <>
              {t("Los meses")}{" "}
              <span className="num-strong">
                {(pnl.data.meses_declarados ?? [])
                  .map((m) => bucketsDe("mes", anio)[m - 1]?.label ?? m)
                  .join(", ")}
              </span>{" "}
              {t("salen de tus contratos, tu planilla y lo que declaraste, no de la hoja. Lo anterior al corte no se toca: es real.")}
            </>
          ) : (
            t("Nadie ha declarado hasta dónde llega lo real de este año, así que no hay meses que proyectar desde lo declarado y la tabla sigue siendo la de tu hoja.")
          )}
        </p>
      ) : null}

      {armado ? (
        <div role="status" className="space-y-1 rounded-md border border-brand/35 bg-brand-soft px-2.5 py-2 text-2xs leading-5">
          <p>
            <strong className="font-medium">{t("Este P&L está armado.")}</strong>{" "}
            {t("Los meses")}{" "}
            <span className="num-strong">
              {armado.meses_ensamblados.map((m) => bucketsDe("mes", anio)[m - 1]?.label ?? m).join(", ")}
            </span>{" "}
            {t("salen de tu libro y tu maestro, no de tu hoja. Pasa el cursor por una cifra para ver qué dijo cada uno.")}{" "}
            <Link to="../que-va-a-finanzas" className="underline underline-offset-2">{t("Qué va a Finanzas")}</Link>
          </p>
          {armado.avisos.map((a) => <p key={a}>· {a}</p>)}
        </div>
      ) : null}

      <WorkPanel
        actions={
          <>
            {/*
              EL MANDO VA EN LA CABECERA DE LA TABLA Y NO EN LA BARRA DE ARRIBA.
              La barra pregunta QUÉ mirar —año, sociedad, granularidad— y afecta a
              las nueve pestañas de Finanzas; esto cambia de dónde sale una de
              ellas. Puesto arriba habría parecido que el forecast entero cambia
              de fuente, cuando lo que cambia es este estado de resultados.
            */}
            <label className="flex items-center gap-1.5 text-2xs text-muted-foreground">
              {t("Los meses futuros, desde")}
              <select
                value={fuente}
                onChange={(e) => setFuente(e.target.value as FuentePnl)}
                className={cn(
                  "h-7 rounded-md border bg-surface px-2 text-xs",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                )}
              >
                <option value="hoja">{t("mi hoja de gestión")}</option>
                <option value="declarado">{t("lo que tengo declarado")}</option>
              </select>
            </label>
            <BotonDescargar recurso="pnl" filtros={{ anio, pais: paisQuery }} />
          </>
        }
        title={`Estado de resultados ${anio}`}
        meta={<span className="text-2xs text-muted-foreground">{t("Mes a mes, por línea · importes en USD")}</span>}
        footnote={t("Los costos van en positivo, como en tu hoja, y el subtotal los resta. Uno negativo es una devolución o un ajuste: resta del costo, igual que en la fila de total de tu hoja.")}
      >
        <DataState
          loading={pnl.isLoading}
          error={pnl.error}
          empty={(pnl.data?.cuentas ?? []).length === 0}
          emptyMessage={t("No hay estado de resultados para este año y país.")}
          onRetry={() => void pnl.refetch()}
          rows={10}
        >
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[18rem]">{t("Concepto")}</TableHead>
                  {buckets.map((b, i) => (
                    <TableHead
                      key={b.label}
                      title={b.meses.some((m) => armados.has(m))
                        ? `${b.nombre} · ${t("sale de tu libro y tu maestro")}` : b.nombre}
                      className={cn("w-24 text-right", i === bucketReal && "corte-real",
                                    b.meses.some((m) => armados.has(m)) && "text-brand")}
                    >
                      {b.label}
                    </TableHead>
                  ))}
                  <TableHead className="w-28 text-right">{t("Total")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(pnl.data?.cuentas ?? []).map((cuenta) => (
                  <React.Fragment key={cuenta.account}>
                    <TableRow className="bg-surface-sunken font-semibold">
                      {/*
                        COMENTAR UNA CIFRA, y se ancla a la CUENTA y no a la
                        celda de un mes. Cesar pidió poder «dejar comentarios al
                        encargado de cosas que no nos cuadren»: lo que no cuadra
                        es «el costo de ventas de este año», y quien lo mira
                        puede estar en vista trimestral o anual — anclarlo al mes
                        haría que el mismo comentario apareciera o no según la
                        granularidad elegida. Ver `components/Comentarios.tsx`.
                      */}
                      <TableCell>
                        <span className="flex items-center gap-1.5">
                          {nombreCuenta(cuenta.account)}
                          <BotonComentario
                            ancla={{
                              ambito: "cifra",
                              referencia: refCuenta(cuenta.account, anio),
                              rotulo: `${nombreCuenta(cuenta.account)} · ${anio}`,
                              anio,
                            }}
                          />
                        </span>
                      </TableCell>
                      {agrupar(serieMensual(cuenta.meses), granularidad, anio).map((b, i) => (
                        <TableCell key={b.label} className={cn("num", i === bucketReal && "corte-real")}>
                          {formatAmount(b.valor)}
                        </TableCell>
                      ))}
                      <TableCell className="num">{formatAmount(cuenta.total)}</TableCell>
                    </TableRow>
                    {cuenta.lineas.map((linea) => (
                      <TableRow key={linea.sub_account}>
                        <TableCell className="pl-6 text-2xs text-muted-foreground">
                          {linea.sub_account}
                        </TableCell>
                        {agrupar(serieMensual(linea.meses), granularidad, anio).map((b, i) => (
                          <TableCell
                            key={b.label}
                            title={origenDe(cuenta.account, linea.sub_account, b.meses)}
                            className={cn("num text-2xs text-muted-foreground",
                                          i === bucketReal && "corte-real",
                                          origenDe(cuenta.account, linea.sub_account, b.meses)
                                            && "underline decoration-dotted underline-offset-2")}
                          >
                            {b.valor === 0 ? "" : formatAmount(b.valor)}
                          </TableCell>
                        ))}
                        <TableCell className="num text-2xs">{formatAmount(linea.total)}</TableCell>
                      </TableRow>
                    ))}
                    {cuenta.account === "Cost of Goods Sold (COGS)" ? (
                      <Subtotal titulo={t("Margen bruto")}
                                serie={agrupar(serieMensual(pnl.data?.subtotales.margen_bruto.meses), granularidad, anio)}
                                total={pnl.data?.subtotales.margen_bruto.total ?? 0}
                                pct={pnl.data?.indicadores.margen_bruto_pct ?? null}
                                bucketReal={bucketReal} />
                    ) : null}
                    {cuenta.account === "G&A Expenses" ? (
                      <Subtotal titulo={t("EBITDA")}
                                serie={agrupar(serieMensual(pnl.data?.subtotales.ebitda.meses), granularidad, anio)}
                                total={pnl.data?.subtotales.ebitda.total ?? 0}
                                pct={pnl.data?.indicadores.ebitda_pct ?? null}
                                bucketReal={bucketReal}
                                destacar />
                    ) : null}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
          <LeyendaDelCorte corte={corte} bucketReal={bucketReal} className="pt-2" />
          <p className="pt-2 text-2xs leading-4 text-muted-foreground">
            {t("El EBITDA es antes de impuestos, financiamiento e inversiones: esas cuentas aparecen abajo pero NO restan en el subtotal. Cambiar eso sería reportar otra cosa con el nombre de EBITDA.")}</p>
        </DataState>
      </WorkPanel>

      <PnlPorArea anio={anio} pais={pais} paisQuery={paisQuery} />
    </div>
  )
}

/**
 * EL P&L POR ÁREA, desde el libro gerencial. Cesar, 2026-10-04: «ejecuta el
 * P&L por área». Va al pie del P&L de la hoja y dice su fuente: el centro de
 * costo de cada línea de asiento. «Sin área» no se reparte, se enseña.
 */
function PnlPorArea({ anio, pais, paisQuery }: { anio: number; pais: string | null; paisQuery?: string }) {
  const { activeTenantId } = useAuth()
  const q = useQuery({
    queryKey: ["finance-pnl-por-area", activeTenantId, anio, pais],
    queryFn: () => fetchPnlPorArea(anio, paisQuery),
    enabled: Boolean(activeTenantId),
  })
  const d = q.data
  const filas: Array<{ k: "ingreso" | "costo" | "gasto" | "resultado"; etiqueta: string }> = [
    { k: "ingreso", etiqueta: t("Ventas") }, { k: "costo", etiqueta: t("Costo de ventas") },
    { k: "gasto", etiqueta: t("Gastos") }, { k: "resultado", etiqueta: t("Resultado") },
  ]
  return (
    <WorkPanel
      title={`${t("P&L por área")} ${anio}`}
      meta={<span className="text-2xs text-muted-foreground">{t("Según el libro gerencial, por centro de costo · USD")}{d?.sin_cambio ? ` · ${d.sin_cambio} ${t("líneas sin tipo de cambio")}` : ""}</span>}
      footnote={d?.nota}
    >
      <DataState loading={q.isLoading} error={q.error} empty={Boolean(d) && (d?.areas.length ?? 0) === 0}
                 emptyMessage={t("El libro no tiene asientos de resultado este año.")}
                 emptyHint={t("Cuando los comprobantes se aprueben con su área en Revisión, el P&L por área aparece aquí.")}
                 onRetry={() => void q.refetch()} rows={4}>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[16rem]">{t("Área")}</TableHead>
                <TableHead className="w-28 text-right">{t("Ventas")}</TableHead>
                <TableHead className="w-28 text-right">{t("Costo de ventas")}</TableHead>
                <TableHead className="w-28 text-right">{t("Gastos")}</TableHead>
                <TableHead className="w-28 text-right">{t("Resultado")}</TableHead>
                <TableHead className="w-24 text-right">{t("Margen")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(d?.areas ?? []).map((a) => (
                <TableRow key={a.codigo ?? "__sin_area__"} className={cn(!a.codigo && "text-muted-foreground")}>
                  <TableCell>
                    {a.codigo ? <span className="code mr-1.5 text-2xs text-muted-foreground">{a.codigo}</span> : null}
                    {a.nombre}
                  </TableCell>
                  <TableCell className="num">{formatAmount(a.total_ingreso)}</TableCell>
                  <TableCell className="num">{formatAmount(a.total_costo)}</TableCell>
                  <TableCell className="num">{formatAmount(a.total_gasto)}</TableCell>
                  <TableCell className={cn("num font-medium", a.total_resultado < 0 && "text-destructive")}>{formatAmount(a.total_resultado)}</TableCell>
                  <TableCell className="num">{a.total_ingreso ? formatPercent(a.total_resultado / a.total_ingreso, 1) : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <p className="px-2.5 pb-2 pt-1 text-2xs text-muted-foreground">
          {filas.map((f) => f.etiqueta).join(" · ")}: {t("acumulado del año. Un área que sólo tiene gasto —Administración, Tecnología— sale con resultado negativo: es el precio de la estructura, no una pérdida de esa área.")}
        </p>
      </DataState>
    </WorkPanel>
  )
}

/** Cifra de portada, con su variación contra el año anterior. */
function Subtotal({
  titulo, serie, total, pct, bucketReal, destacar,
}: {
  titulo: string
  serie: Bucket[]
  total: number
  pct: number | null
  /** Columna donde cae la linea del corte, o `null`/`-1` si no se pinta. */
  bucketReal: number | null
  destacar?: boolean
}) {
  return (
    <TableRow className={cn("font-semibold", destacar ? "bg-brand-soft" : "bg-surface-sunken/60")}>
      <TableCell>
        {titulo}
        {pct === null ? null : (
          <span className="ml-1.5 text-2xs font-normal text-muted-foreground">
            {formatPercent(pct, 1)}
          </span>
        )}
      </TableCell>
      {serie.map((b, i) => (
        <TableCell
          key={b.label}
          className={cn("num", b.valor < 0 && "text-destructive",
                        i === bucketReal && "corte-real")}
        >
          {formatAmount(b.valor)}
        </TableCell>
      ))}
      <TableCell className={cn("num", total < 0 && "text-destructive")}>
        {formatAmount(total)}
      </TableCell>
    </TableRow>
  )
}
